import { useJobBoardStore } from '../store/job-board.store';
import { useJobQualitySurveyStore } from '../store/job-quality-survey.store';
import { useApplyConfirmationStore } from '../store/apply-confirmation.store';
import { useUserEvents, USER_EVENTS } from '../store/user-events.store';
import { setAuthState } from './auth';
import { openLink } from '../utils/helpers.js';

/**
 * The single way the job board sends a seeker to an employer's site.
 *
 * Nine call sites used to do this by hand and only two of them recorded anything, so a plain
 * Apply click was invisible to the rest of the product. Everything that has to happen on a
 * redirect lives here now, and EN-621's "Did you apply?" prompt can rely on it.
 */
export function useExternalApply() {
	const { isAuthenticated } = setAuthState();

	/**
	 * @param {object} job         at minimum `{ provider_job_id, apply_url }`
	 * @param {string} [applyUrl]  overrides `job.apply_url`, for the AutoApply paths that
	 *                             capture the URL before an await to avoid a stale ref read
	 * @param {object} [options]
	 * @param {boolean} [options.askIfApplied=true]  false skips the "Did you apply?" prompt, for
	 *                             an AutoApply Queue application, whose own "Mark as Applied"
	 *                             records it (EN-1248). The prompt's Yes would add a Job Tracker
	 *                             card from the Job Board and leave the queue row as it was.
	 */
	const openExternalApply = (job, applyUrl = null, { askIfApplied = true } = {}) => {
		const url = applyUrl ?? job?.apply_url;

		if (!url) {
			return;
		}

		// First, and before any await: opening outside the user-activation task is what Safari
		// blocks. A blocked popup would also arm a prompt for a redirect that never happened.
		openLink(url, true);

		// The seeker has acted, so they are no longer in the "engaged but not converting"
		// segment the quality survey targets.
		useJobQualitySurveyStore().resetCounters();

		useJobBoardStore().trackActivity(job?.provider_job_id, 'click');

		if (!isAuthenticated()) {
			return;
		}

		useUserEvents().trackEvent(USER_EVENTS.JOB_APPLIED);

		// Nothing to confirm for a job already marked applied — the seeker answered this in an
		// earlier session, or AutoApply Tier 2 marked it when it redirected.
		if (!askIfApplied || job?.is_applied) {
			return;
		}

		useApplyConfirmationStore().enqueue(job);
	};

	return { openExternalApply };
}
